/**
 * One criterion: "understand the lineage, then judge". Ported from the #5 prototype's
 * variant C, which was Gareth's choice after all three variants were built.
 *
 * A horizontal version rail shows the whole lineage — node size encodes label volume
 * (question 3), the numbers on the edges are consecutive-version similarity so you can see
 * *where* drift happened, and clicking a node diffs it against the current version. The pairwise
 * matrix makes non-transitivity visible: consecutive steps all ≥ 0.892 while v1↔v9 is 0.802
 * is a shape you can see rather than be told about. Sentence/label pairs render INLINE under
 * the rail — neither modal nor docked panel (question 4).
 *
 * Two things the prototype could not know:
 *
 * - **Eligibility is per version, not per pair** (ADR-0001), so the verdict buttons act on the
 *   *older* version of the selected pair, and only when the newer side is the current version.
 *   An arbitrary pair like (v1, v4) still diffs — that is the understanding tool — but cannot
 *   write eligibility, or transitivity re-enters through the back door.
 * - **The threshold is gone.** #9 measured that no cut-off separates equivalent from revised,
 *   so nothing here colours by a cut-off.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlaceIcon from '@mui/icons-material/Place';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import {
  BulkEligibilityAction,
  CriterionComparison,
  EligibilityStatus,
  PolicyVersionState,
} from '../../../types';
import { DiffMode, DiffPane } from './diff/DiffPane';
import { LabelSamplesList } from './LabelSamples';
import { defaultPairIds, resolvePair } from './pairSelection';
import { PinAffordance, pinAffordance, pinWarning } from './pinAffordance';
import { copyToClipboard, formatDate, Similarity, similarityShade, VerdictButtons } from './shared';

const nodeSize = (labelCount: number, max: number) => {
  if (labelCount === 0) return 22;
  return 26 + Math.round((labelCount / max) * 34);
};

/**
 * Room above every rail bubble for the pin control, so the rail does not reflow when a
 * selection moves. The edge columns get the same padding so the connecting lines stay
 * level with the bubbles they join.
 */
const PIN_SLOT = 32;

/**
 * The control in the slot above a rail bubble. See docs/specs/policy-version-pinning.md §6.1.
 *
 * The pinned marker is a red map pin with a push-pin glyph inside, and it IS the unpin
 * button -- the one control here reachable without selecting first. An unpinned selected
 * version gets an outlined push-pin offering to pin. Anything else renders the empty slot.
 */
const PinControl = ({
  affordance,
  busy,
  onPin,
  onUnpin,
}: {
  affordance: PinAffordance;
  busy: boolean;
  onPin: () => void;
  onUnpin: () => void;
}) => {
  if (affordance.kind === 'none') return <Box sx={{ height: PIN_SLOT }} />;

  if (affordance.kind === 'unpin') {
    return (
      <Tooltip title={affordance.tooltip}>
        <IconButton
          size="small"
          disabled={busy}
          onClick={onUnpin}
          aria-label="Unpin this version"
          sx={{ p: 0, height: PIN_SLOT, width: PIN_SLOT, position: 'relative' }}
        >
          <PlaceIcon sx={{ fontSize: 30, color: 'error.main' }} />
          <PushPinIcon
            sx={{
              fontSize: 11,
              color: 'common.white',
              position: 'absolute',
              // Centred in the head of the map pin, which sits in the upper part of the
              // glyph's bounding box.
              top: 7,
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          />
        </IconButton>
      </Tooltip>
    );
  }

  const disabled = busy || affordance.kind === 'disabled';
  const button = (
    <IconButton
      size="small"
      disabled={disabled}
      onClick={onPin}
      aria-label="Pin this version"
      sx={{ height: PIN_SLOT, width: PIN_SLOT }}
    >
      <PushPinOutlinedIcon sx={{ fontSize: 20 }} />
    </IconButton>
  );
  // A disabled button emits no pointer events, so a tooltip needs a wrapper to hang off.
  return (
    <Tooltip title={affordance.tooltip}>
      <span style={{ display: 'inline-flex' }}>{button}</span>
    </Tooltip>
  );
};

/** "(current)", "(pinned)" or "(current, pinned)" after a version number in a title. */
const sideMarker = (version: PolicyVersionState): string | null => {
  const parts = [version.isCurrent ? 'current' : null, version.isPinned ? 'pinned' : null].filter(
    Boolean
  );
  return parts.length ? `(${parts.join(', ')})` : null;
};

/**
 * A version number in the diff title that copies that version's policy UUID.
 *
 * A real <button> so it is keyboard-reachable and announced as an action, styled back to
 * inline text so the title still reads as "v2 → v8".
 */
const CopyVersionId = ({
  version,
  onCopy,
}: {
  version: PolicyVersionState;
  onCopy: (text: string, description: string) => void;
}) => (
  <Tooltip title={`Copy v${version.version}'s policy UUID`}>
    <Box
      component="button"
      onClick={() => onCopy(version.policyId, `v${version.version} policy ID`)}
      sx={{
        background: 'none',
        border: 'none',
        p: 0,
        font: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
        textDecoration: 'underline',
        textDecorationStyle: 'dotted',
        textUnderlineOffset: '3px',
        '&:hover': { textDecorationStyle: 'solid' },
      }}
    >
      v{version.version}
    </Box>
  </Tooltip>
);

const STATUS_HELP: Record<EligibilityStatus, string> = {
  current: 'The reference version. Eligible unless you exclude it.',
  included: 'You ruled these labels safe to use as eval ground truth.',
  excluded: 'You ruled these labels out.',
  unjudged: 'Carries labels and nobody has ruled on it. This is the work.',
  no_labels: 'No labels, so it contributes nothing either way. Never needs looking at.',
  unusable: 'Blank rubric text: nothing can be diffed, embedded or judged.',
};

interface Props {
  comparison: CriterionComparison;
  busy: boolean;
  onBack: () => void;
  onSetEligibility: (policyId: string, eligible: boolean) => void;
  onBulk: (action: BulkEligibilityAction) => void;
  /** Pin one version as the rubric Stylus marks with. Nothing to do with eligibility. */
  onPin: (policyId: string) => void;
  onUnpin: () => void;
  onNotify: (message: string, severity: 'success' | 'error') => void;
}

const ComparisonPage = ({
  comparison,
  busy,
  onBack,
  onSetEligibility,
  onBulk,
  onPin,
  onUnpin,
  onNotify,
}: Props) => {
  const versions = useMemo(
    () => [...comparison.versions].sort((a, b) => a.version - b.version),
    [comparison.versions]
  );
  const current = versions.find(version => version.isCurrent) ?? versions[versions.length - 1];

  const cosineFor = useMemo(() => {
    const lookup = new Map<string, number>();
    comparison.similarities.forEach(similarity => {
      lookup.set(`${similarity.versionA}:${similarity.versionB}`, similarity.cosine);
      lookup.set(`${similarity.versionB}:${similarity.versionA}`, similarity.cosine);
    });
    return (a: number, b: number) => lookup.get(`${a}:${b}`);
  }, [comparison.similarities]);

  // Selection is held as policy IDs, not objects. Objects come from a fetch, so keeping
  // references would show stale status and eligibility after a decision is saved and the
  // criterion refetched -- the verdict buttons would not grey out until you clicked
  // something else.
  const [selectedIds, setSelectedIds] = useState<[string, string] | null>(null);
  const setSelection = (older: PolicyVersionState, newer: PolicyVersionState) =>
    setSelectedIds([older.policyId, newer.policyId]);

  // Pin the opening pair once per criterion. Deriving it on every render meant a decision
  // moved the view: the default is the first *unjudged* version, so approving it made the
  // next unjudged version the default and the page jumped to a pair you had not chosen.
  useEffect(() => {
    setSelectedIds(defaultPairIds(comparison.versions));
    // Deliberately keyed on the criterion alone. Re-running on `versions` would restore the
    // jump this exists to prevent, since a saved decision changes `versions`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparison.criteriaId]);

  const [inlineLabels, setInlineLabels] = useState<string | null>(null);
  // Gareth's call. Hunked side-by-side opens first: aligned paragraphs make it easier to
  // see *where* in the rubric something changed, and the admin can switch to the prose
  // renderers for the single-clause edits they read better.
  const [diffMode, setDiffMode] = useState<DiffMode>('pierre-split-word');
  const [rendered, setRendered] = useState(false);

  const pair = useMemo(
    () => resolvePair(comparison.versions, selectedIds),
    [comparison.versions, selectedIds]
  );

  const maxLabels = Math.max(...versions.map(v => v.labelCount), 1);
  // Labelled versions PLUS the current one, even when it carries no labels. The prototype
  // filtered on labelCount > 0 alone, which drops the reference version from the matrix
  // exactly when it is most needed: on `OBSOLETE Uses characters - crafted` -- the largest
  // item in the queue -- the current version has 0 labels, so the matrix both omitted the
  // version being judged against and hid itself for having fewer than three columns.
  const matrixVersions = versions.filter(v => v.labelCount > 0 || v.isCurrent);
  const staleCount = versions.filter(v => v.stale).length;
  // The pinned version has since been ruled different (or is blank). Warn, never block:
  // the verdict and the pin are separate decisions. See pinAffordance.ts.
  const pinMismatch = pinWarning(versions);

  const copy = async (text: string, description: string) => {
    const ok = await copyToClipboard(text);
    onNotify(
      ok ? `Copied ${description}` : 'Could not copy — the clipboard needs a secure context',
      ok ? 'success' : 'error'
    );
  };

  /** Every version's UUID, in version order, for pasting into a query or a notebook. */
  const copyAllPolicyIds = () =>
    copy(
      versions.map(version => version.policyId).join(','),
      `${versions.length} policy version ID${versions.length === 1 ? '' : 's'}`
    );

  /**
   * One click diffs that version against the current one.
   *
   * This replaces click-two-to-compare, where the first click appeared to do nothing
   * (an incomplete pair fell back to the default selection) and picking a version with no
   * labels was effectively unreachable — the matrix only lists labelled-or-current
   * versions, so the rail was the only route and it took two deliberate clicks to get
   * there. Arbitrary pairs are still reachable from the matrix.
   */
  const handleNodeClick = (version: PolicyVersionState) => {
    if (!current) return;
    if (version.policyId === current.policyId) {
      // Nothing to diff against itself. The only diff involving the current version that
      // means anything is the step that produced it, so show that.
      const previous = versions[versions.length - 2];
      if (previous) setSelection(previous, current);
      return;
    }
    setSelection(version, current);
  };

  // Eligibility is a claim about one version relative to the CURRENT one. Judging an
  // arbitrary pair would smuggle transitivity back in.
  const judgeable = pair && current ? pair[1].policyId === current.policyId : false;
  const subject = pair ? pair[0] : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Paper sx={{ p: 2 }}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{
            textTransform: 'none',
            // The title below renders at p: 0, so zeroing the left padding and margin gives
            // the two a shared left edge. The old ml: -0.5 left the arrow 6px in and the
            // text 34px in, aligned with neither.
            ml: 0,
            pl: 0,
            minWidth: 0,
            // Clear of the title rather than crowding it.
            mb: 1.5,
          }}
        >
          All criteria
        </Button>
        <Tooltip title="Click to copy every version's policy UUID as a comma-separated list">
          <Typography
            variant="h6"
            component="button"
            onClick={copyAllPolicyIds}
            sx={{
              // A button for keyboard and screen-reader users, styled back to a heading.
              // Block-level (flex, not inline-flex) so it sits on its own line UNDER the
              // back button rather than flowing after it on a mismatched baseline; the
              // width stays fit-content so only the title itself is the copy target.
              display: 'flex',
              width: 'fit-content',
              alignItems: 'center',
              gap: 0.75,
              background: 'none',
              border: 'none',
              p: 0,
              font: 'inherit',
              color: 'inherit',
              textAlign: 'left',
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
              '&:hover .copy-hint': { opacity: 1 },
            }}
          >
            {comparison.criterionName}
            <ContentCopyIcon
              className="copy-hint"
              fontSize="small"
              sx={{ opacity: 0.35, transition: 'opacity 120ms' }}
            />
          </Typography>
        </Tooltip>
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip size="small" variant="outlined" label={comparison.granularity} />
          <Chip size="small" variant="outlined" label={`${versions.length} versions`} />
          <Chip
            size="small"
            variant="outlined"
            label={`${versions.reduce((sum, v) => sum + v.labelCount, 0).toLocaleString()} labels`}
          />
          <Chip
            size="small"
            color="primary"
            label={`${versions.filter(v => v.status === 'unjudged').length} to judge`}
          />
          {/* Absence is the normal state and is not announced. */}
          {comparison.pin ? (
            <Tooltip
              title={
                <>
                  Stylus marks with v{comparison.pin.version} until this is unpinned; labellers
                  still label against the current version.
                  {pinMismatch
                    ? ' Its labels have since been ruled different from the current rubric — see the warning below.'
                    : ''}
                  <br />
                  Pinned by {comparison.pin.pinnedByName ?? 'an admin'} on{' '}
                  {formatDate(comparison.pin.pinnedAt)}
                  {comparison.pin.reason ? ` · ${comparison.pin.reason}` : ''}
                </>
              }
            >
              <Chip
                size="small"
                // Red is the pin's normal colour. Amber says the pin is now at odds with
                // an eligibility verdict, which is the one state worth a second look.
                color={pinMismatch ? 'warning' : 'error'}
                icon={<PushPinIcon />}
                label={`Pinned to v${comparison.pin.version}`}
              />
            </Tooltip>
          ) : null}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button
              size="small"
              color="success"
              variant="outlined"
              disabled={busy}
              onClick={() => onBulk('include_all')}
              sx={{ textTransform: 'none' }}
            >
              Approve all versions
            </Button>
            <Button
              size="small"
              color="error"
              variant="outlined"
              disabled={busy}
              onClick={() => onBulk('disregard_all')}
              sx={{ textTransform: 'none' }}
            >
              Disregard all versions
            </Button>
          </Box>
        </Box>
      </Paper>

      {staleCount > 0 ? (
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              disabled={busy}
              onClick={() => onBulk('reaffirm_all')}
            >
              Re-affirm {staleCount}
            </Button>
          }
        >
          A newer version has arrived since{' '}
          {staleCount === 1 ? 'a decision was' : 'these decisions were'} made. Nothing has been
          revoked — the labels are still eligible. Read what changed before re-affirming: a version
          scoring 0.996 similar can still forbid what was previously credited.
        </Alert>
      ) : null}

      {/* The pinned version is one eval ground truth no longer counts. Eligibility is judged
          against the current version (ADR-0001) while Stylus marks with the pin, so evals
          for this criterion compare a marker on one rubric against labels judged on another.
          Neither decision is blocked; this makes the mismatch visible so an admin resolves
          it deliberately, one way or the other. */}
      {pinMismatch ? (
        <Alert
          severity="warning"
          icon={<PushPinIcon fontSize="inherit" />}
          action={
            <Button color="inherit" size="small" disabled={busy} onClick={onUnpin}>
              Unpin v{pinMismatch.pinned.version}
            </Button>
          }
        >
          Stylus marks with v{pinMismatch.pinned.version}, but{' '}
          {pinMismatch.pinned.status === 'unusable'
            ? 'its rubric text is blank'
            : `its labels have been ruled different from ${
                pinMismatch.current ? `v${pinMismatch.current.version}` : 'the current version'
              }`}
          . Eval ground truth is judged against the current version, so evals for this criterion
          compare marking on one rubric against labels judged on another. Unpin to let Stylus follow
          the current version, or reverse the verdict if the rubrics are in fact equivalent.
        </Alert>
      ) : null}

      {/* The version rail */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          {versions.length} versions · {versions.filter(v => v.labelCount > 0).length} carry labels
          · click any one to diff it against the current version
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', overflowX: 'auto', pb: 1, pt: 1 }}>
          {versions.map((version, index) => {
            const size = nodeSize(version.labelCount, maxLabels);
            const isSelected = pair?.some(side => side.policyId === version.policyId) ?? false;
            const previous = versions[index - 1];
            const edge = previous ? cosineFor(previous.version, version.version) : undefined;
            return (
              <Box key={version.policyId} sx={{ display: 'flex', alignItems: 'center' }}>
                {previous ? (
                  <Box sx={{ textAlign: 'center', px: 0.5, minWidth: 62, pt: `${PIN_SLOT}px` }}>
                    <Box sx={{ height: 2, bgcolor: 'grey.400' }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: 10,
                        px: 0.3,
                        borderRadius: 0.5,
                        backgroundColor:
                          edge === undefined ? undefined : similarityShade(edge, 0.5),
                      }}
                    >
                      {edge === undefined ? '·' : edge.toFixed(3)}
                    </Typography>
                  </Box>
                ) : null}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {/* Selection is a PAIR, so this can show on two bubbles at once. That is
                      deliberate: the current version is always the right-hand side, and
                      pinning it -- "hold here when the next version lands" -- is the primary
                      use, so it must be reachable without a second click. */}
                  <PinControl
                    affordance={pinAffordance(version, isSelected)}
                    busy={busy}
                    onPin={() => onPin(version.policyId)}
                    onUnpin={onUnpin}
                  />
                  <Tooltip
                    title={
                      <>
                        v{version.version} · {formatDate(version.creationDate)} ·{' '}
                        {version.labelCount.toLocaleString()} labels ·{' '}
                        {version.status.replace('_', ' ')}
                        {version.stale ? ' · STALE' : ''}
                        {version.isPinned ? ' · PINNED' : ''}
                        <br />
                        {STATUS_HELP[version.status]}
                        <br />
                        Click to diff this version against the current one.
                      </>
                    }
                  >
                    <Box
                      onClick={() => handleNodeClick(version)}
                      sx={{
                        width: size,
                        height: size,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        flexShrink: 0,
                        fontSize: 12,
                        fontWeight: 700,
                        border: '2px solid',
                        borderColor: version.isCurrent ? 'primary.main' : 'grey.400',
                        // Selection does NOT recolour the node. The fill always encodes
                        // eligibility state, so an included version stays green while
                        // selected; the ring alone says "this is selected". Overloading the
                        // fill with both meant losing the status of whatever you clicked.
                        bgcolor: version.eligible
                          ? 'success.light'
                          : version.status === 'excluded'
                            ? 'error.light'
                            : version.labelCount > 0
                              ? 'warning.light'
                              : 'grey.100',
                        color: 'text.primary',
                        outline: isSelected ? '3px solid' : 'none',
                        outlineColor: 'text.primary',
                        outlineOffset: isSelected ? '2px' : 0,
                      }}
                    >
                      {version.version}
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>
        <Typography variant="caption" color="text.secondary">
          Node size = label volume · blue border = current version · dark halo = selected · fill
          shows state: green = eligible, red = excluded, amber = carries labels and unjudged, grey =
          no labels · red pin = the version Stylus marks with · numbers on the edges are
          consecutive-version similarity, so you can see <em>where</em> the drift happened
        </Typography>
      </Paper>

      {/* Pairwise matrix — non-transitivity made visible */}
      {matrixVersions.length > 2 ? (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Pairwise similarity across labelled versions
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Click a cell to diff that pair. Triage ordering only — it never decides eligibility.
          </Typography>
          <Box sx={{ display: 'inline-block', overflowX: 'auto' }}>
            <Box sx={{ display: 'flex' }}>
              <Box sx={{ width: 44 }} />
              {matrixVersions.map(version => (
                <Box key={version.policyId} sx={{ width: 64, textAlign: 'center', fontSize: 12 }}>
                  v{version.version}
                </Box>
              ))}
            </Box>
            {matrixVersions.map(rowVersion => (
              <Box key={rowVersion.policyId} sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: 44, fontSize: 12 }}>v{rowVersion.version}</Box>
                {matrixVersions.map(columnVersion => {
                  const same = rowVersion.policyId === columnVersion.policyId;
                  const value = same ? 1 : cosineFor(rowVersion.version, columnVersion.version);
                  // The matrix is symmetric, so a selected pair occupies two cells. Both
                  // are highlighted: the eye lands on either half depending on which
                  // version you were tracking, and marking only one reads as a glitch.
                  const isSelected =
                    !same &&
                    pair !== null &&
                    ((pair[0].policyId === rowVersion.policyId &&
                      pair[1].policyId === columnVersion.policyId) ||
                      (pair[1].policyId === rowVersion.policyId &&
                        pair[0].policyId === columnVersion.policyId));
                  return (
                    <Box
                      key={columnVersion.policyId}
                      onClick={() => !same && setSelection(rowVersion, columnVersion)}
                      sx={{
                        width: 64,
                        height: 30,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: 'monospace',
                        fontSize: 11,
                        fontWeight: isSelected ? 700 : 400,
                        cursor: same ? 'default' : 'pointer',
                        bgcolor: same
                          ? 'grey.200'
                          : value === undefined
                            ? 'grey.50'
                            : similarityShade(value, 0.55),
                        // An inset ring rather than a background change, so the similarity
                        // shade underneath stays readable and comparable with its neighbours.
                        boxShadow: isSelected
                          ? theme => `inset 0 0 0 2px ${theme.palette.primary.main}`
                          : 'none',
                        color: isSelected ? 'primary.main' : 'text.primary',
                        border: '1px solid #fff',
                      }}
                    >
                      {same ? '—' : value === undefined ? '·' : value.toFixed(3)}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Paper>
      ) : null}

      {/* The selected pair */}
      {pair && subject ? (
        <Paper sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              // The title block sits next to the similarity chip and verdict buttons; gap 2
              // let them crowd it, and mb 1.5 put it close on top of the diff.
              columnGap: 3,
              rowGap: 1.5,
              mb: 2.5,
              flexWrap: 'wrap',
            }}
          >
            <Box>
              {/* Each version number copies its own policy UUID. This lives here rather
                  than on the rail bubbles, whose click now picks the pair to diff.
                  Generous column gaps and a wrap rule: the two copy controls are separate
                  targets, so they need visible space between them and the arrow, and this
                  box shares a flex row with the similarity chip and verdict buttons, so it
                  does get squeezed on a narrow window. */}
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  columnGap: 1.25,
                  rowGap: 0.5,
                  lineHeight: 1.5,
                  mb: 0.75,
                }}
              >
                <CopyVersionId version={pair[0]} onCopy={copy} />
                {sideMarker(pair[0]) ? (
                  <Box
                    component="span"
                    sx={{ fontWeight: 400, color: 'text.secondary', whiteSpace: 'nowrap' }}
                  >
                    {sideMarker(pair[0])}
                  </Box>
                ) : null}
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  →
                </Box>
                <CopyVersionId version={pair[1]} onCopy={copy} />
                {sideMarker(pair[1]) ? (
                  <Box
                    component="span"
                    sx={{ fontWeight: 400, color: 'text.secondary', whiteSpace: 'nowrap' }}
                  >
                    {sideMarker(pair[1])}
                  </Box>
                ) : null}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', lineHeight: 1.6 }}
              >
                {subject.labelCount.toLocaleString()} labels on v{subject.version} at stake ·{' '}
                {subject.status.replace('_', ' ')}
                {subject.stale ? ' · stale' : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Similarity cosine={cosineFor(pair[0].version, pair[1].version)} size="medium" />
              <VerdictButtons
                eligible={subject.eligible}
                decided={subject.status === 'included' || subject.status === 'excluded'}
                disabled={busy || !judgeable || subject.status === 'unusable'}
                disabledReason={
                  subject.status === 'unusable'
                    ? 'Blank rubric text — nothing can be judged about it.'
                    : !judgeable
                      ? `Eligibility is judged against the current version (v${current?.version}). This pair is for understanding where drift happened; select v${subject.version} and the current version to rule on it.`
                      : undefined
                }
                onVerdict={eligible => onSetEligibility(subject.policyId, eligible)}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {pair.map(version => (
              <Chip
                key={version.policyId}
                label={`v${version.version} labels (${version.labelCount.toLocaleString()})`}
                variant={inlineLabels === version.policyId ? 'filled' : 'outlined'}
                color={inlineLabels === version.policyId ? 'primary' : 'default'}
                onClick={() =>
                  setInlineLabels(prev => (prev === version.policyId ? null : version.policyId))
                }
                size="small"
              />
            ))}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={rendered ? 'rendered' : 'diff'}
              onChange={(_event, value) => value && setRendered(value === 'rendered')}
              sx={{ ml: 'auto' }}
            >
              <ToggleButton value="diff" sx={{ textTransform: 'none' }}>
                Diff (raw)
              </ToggleButton>
              <Tooltip title="The rubric as the labeller actually read it, rendered as Markdown (#13).">
                <ToggleButton value="rendered" sx={{ textTransform: 'none' }}>
                  Rendered
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          </Box>

          {inlineLabels ? (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                bgcolor: 'grey.50',
                borderRadius: 1,
                // Taller than the prototype's 260 now the list is searchable: a search box
                // plus its result count plus two label rows needed more than that.
                maxHeight: 420,
                overflow: 'auto',
              }}
            >
              {/* Keyed on the version, so switching chips remounts and clears the search
                  box. Carrying a query across versions would leave a stale "3 of 576 match"
                  reading against a list it was never run over. */}
              <LabelSamplesList
                key={inlineLabels}
                version={pair.find(v => v.policyId === inlineLabels) ?? pair[0]}
                labels={comparison.labelsByPolicyId[inlineLabels] ?? []}
              />
            </Box>
          ) : null}

          {rendered ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              {pair.map(version => (
                <Box key={version.policyId}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    v{version.version}
                    {sideMarker(version) ? ` ${sideMarker(version)}` : ''}
                  </Typography>
                  <Box sx={{ fontSize: '0.9rem', '& p': { mt: 0 } }}>
                    <ReactMarkdown remarkPlugins={[remarkBreaks]}>
                      {version.text ?? ''}
                    </ReactMarkdown>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <DiffPane older={pair[0]} newer={pair[1]} mode={diffMode} onModeChange={setDiffMode} />
          )}
        </Paper>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">
            This criterion has no labelled non-current version — nothing to judge.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ComparisonPage;
